'use client';

import { useState } from 'react';
import { trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import { MessageBody } from './MessageBody';

export interface RetainedMessageBodyProps {
  /** The stable internal id of the retained message. */
  clinicMessageId: number;
  /** The snippet captured at ingest. Shown before the body is asked for. */
  snippet?: string | null;
  /** False once retention purged the body — then there is nothing to open. */
  hasStoredContent: boolean;
  className?: string;
}

/**
 * A message body the mailbox can no longer produce, read from the workspace's own copy.
 *
 * Opened on request rather than with the page. Every read is a capability check and an
 * audit event on the server, and a conversation that fetched all of its bodies on open
 * would be the bulk content read `clinicContent` deliberately does not offer, arriving
 * one call at a time. Asking is also the honest interaction: this is the workspace's
 * retained copy of somebody's correspondence, and a deliberate press is a better record
 * of intent than a page load.
 *
 * Calm by construction — a mailbox forgetting a thread is not an error and gets no error
 * colour. The line explains the state, the control offers the copy, and a purged body
 * says so plainly instead of failing.
 */
export function RetainedMessageBody({
  clinicMessageId,
  snippet,
  hasStoredContent,
  className,
}: RetainedMessageBodyProps) {
  const [requested, setRequested] = useState(false);

  const contentQuery = trpc.clinicContent.getMessageContent.useQuery(
    { messageId: clinicMessageId, variant: 'sanitized_html' },
    { enabled: requested, staleTime: 5 * 60_000 }
  );

  const content = contentQuery.data;

  if (content?.available) {
    return <MessageBody html={content.body} className={className} />;
  }

  return (
    <div className={cn('space-y-2', className)}>
      {snippet && <p className="text-sm leading-relaxed text-murmur">{snippet}</p>}

      <p className="text-sm leading-relaxed text-murmur">
        {hasStoredContent
          ? 'This message is no longer in the mailbox. The workspace kept a copy.'
          : 'This message is no longer in the mailbox, and the kept copy has passed the workspace retention window.'}
      </p>

      {hasStoredContent && !content && (
        <Button
          variant="outline"
          size="sm"
          disabled={contentQuery.isFetching}
          onClick={() => setRequested(true)}
        >
          {contentQuery.isFetching ? 'Opening the kept copy' : 'Open the kept copy'}
        </Button>
      )}

      {content && !content.available && (
        <p className="text-sm leading-relaxed text-murmur">
          The kept copy is not available to open.
        </p>
      )}
    </div>
  );
}
